import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, StatCard, StatGridSkeleton, EmptyState } from "@/components/common";
import { TASK_STATUSES, item } from "@/lib/constants";
import { fmtCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { CheckCircle2, ListTodo, AlertTriangle, FolderKanban, Download, Printer } from "lucide-react";

export default function Reports() {
  const { data, isLoading } = useQuery({ queryKey: ["reports"], queryFn: async () => (await api.get("/reports/summary")).data });
  if (isLoading || !data) return <div><PageHeader title="Raporlar" /><StatGridSkeleton /></div>;

  const statusChart = TASK_STATUSES.map((s) => ({ name: s.label, value: data.task_by_status[s.id] || 0, color: s.color })).filter((d) => d.value);
  const empChart = (data.employee_performance || []).filter((e) => e.assigned > 0).map((e) => ({ name: e.name.split(" ")[0], atanan: e.assigned, tamamlanan: e.completed }));

  const exportCsv = () => {
    const rows = [["Çalışan", "Atanan", "Tamamlanan", "Oran %"], ...data.employee_performance.map((e) => [e.name, e.assigned, e.completed, e.rate])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "calisan-performans.csv"; a.click();
  };

  return (
    <div>
      <PageHeader title="Raporlar" subtitle="Gerçek veritabanı hesaplamaları"
        actions={<div className="flex gap-2"><Button variant="outline" onClick={exportCsv} data-testid="export-csv-button"><Download className="h-4 w-4 mr-1.5" /> CSV</Button><Button variant="outline" onClick={() => window.print()} data-testid="print-button"><Printer className="h-4 w-4 mr-1.5" /> Yazdır</Button></div>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={ListTodo} label="Toplam görev" value={data.total_tasks} tone="indigo" />
        <StatCard icon={CheckCircle2} label="Tamamlanan" value={data.completed_tasks} tone="emerald" />
        <StatCard icon={AlertTriangle} label="Geciken" value={data.overdue_count} tone="rose" />
        <StatCard icon={FolderKanban} label="Toplam proje" value={data.project_progress?.length || 0} tone="sky" />
      </div>

      <Tabs defaultValue="tasks">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="tasks" data-testid="rep-tab-tasks">Görevler</TabsTrigger>
          <TabsTrigger value="employees" data-testid="rep-tab-employees">Çalışan Performansı</TabsTrigger>
          <TabsTrigger value="projects" data-testid="rep-tab-projects">Proje İlerlemesi</TabsTrigger>
          <TabsTrigger value="time" data-testid="rep-tab-time">Zaman</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-4">
          <Card className="p-5">
            <h3 className="font-semibold mb-4">Duruma göre görev dağılımı</h3>
            {statusChart.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusChart}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} /><Tooltip />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>{statusChart.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState title="Veri yok" />}
          </Card>
        </TabsContent>

        <TabsContent value="employees" className="mt-4">
          <Card className="p-5">
            <h3 className="font-semibold mb-4">Çalışan performansı</h3>
            {empChart.length ? (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={empChart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} /><Tooltip />
                    <Bar dataKey="atanan" fill="#94a3b8" radius={[6, 6, 0, 0]} /><Bar dataKey="tamamlanan" fill="#10b981" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {data.employee_performance.filter((e) => e.assigned > 0).map((e) => (
                    <div key={e.name} className="flex items-center gap-3"><span className="text-sm w-32 truncate">{e.name}</span><Progress value={e.rate} className="h-2 flex-1" /><span className="text-xs w-10 text-right">{e.rate}%</span></div>
                  ))}
                </div>
              </>
            ) : <EmptyState title="Veri yok" />}
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="mt-4">
          <Card className="p-5 space-y-3">
            <h3 className="font-semibold mb-2">Proje ilerlemesi</h3>
            {data.project_progress?.length ? data.project_progress.map((p) => (
              <div key={p.name}><div className="flex justify-between text-sm mb-1"><span>{p.name}</span><span className="text-muted-foreground">{p.completed}/{p.total}</span></div><Progress value={p.progress} className="h-2" /></div>
            )) : <EmptyState title="Proje yok" />}
          </Card>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <Card className="p-5">
            <h3 className="font-semibold mb-4">Projeye göre harcanan zaman (saat)</h3>
            {data.time_report?.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.time_report}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip />
                  <Bar dataKey="hours" fill="#4f46e5" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState title="Zaman verisi yok" />}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
